import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";

import {
  HydratedOpportunity,
  CreateOpportunityParams,
  UpdateOpportunityParams,
  OpportunityStatus,
  OpportunityStatistics,
  OpportunityPeopleFilterCounts,
  OpportunityBoardFilters,
  ListOpportunitiesResponseSchema,
  GetOpportunityResponseSchema,
  CreateOpportunityResponseSchema,
  UpdateOpportunityResponseSchema,
  GetOpportunityStatisticsResponseSchema,
  GetOpportunityPeopleFilterCountsResponseSchema,
  GetOpportunityCompanyFilterOptionsResponseSchema,
  OpportunityTimingResponseSchema,
  ListOpportunityTasksResponseSchema,
  OpportunityStageRequirementsErrorSchema,
  AgentTask,
  HydratedCompany,
  Location,
  type OpportunityTimingResponse,
} from "@forgepoint/shared";
import { useCompaniesStore } from "@/modules/companies/companiesStore";
import { useLocationsStore } from "@/modules/locations/locationsStore";

interface OpportunityLoadingState {
  isLoadingGet: boolean;
  isLoadingSave: boolean;
  isLoadingArchive: boolean;
  getError: string | null;
  saveError: string | null;
  archiveError: string | null;
}

interface OpportunityTimingLoadingState {
  isLoading: boolean;
  error: string | null;
}

interface OpportunityTasksLoadingState {
  isLoading: boolean;
  error: string | null;
}

interface OpportunitiesStore {
  opportunities: Map<string, HydratedOpportunity>;
  opportunityLoadingStates: Map<string, OpportunityLoadingState>;
  opportunityTimings: Map<string, OpportunityTimingResponse>;
  opportunityTimingLoadingStates: Map<string, OpportunityTimingLoadingState>;
  opportunityTasks: Map<string, AgentTask[]>;
  opportunityTasksLoadingStates: Map<string, OpportunityTasksLoadingState>;
  statistics: OpportunityStatistics | null;
  statisticsLoading: boolean;
  peopleFilterCounts: OpportunityPeopleFilterCounts | null;
  companyFilterOptionsLoading: boolean;
  listLoading: boolean;
  listError: string | null;
  total: number;

  getHydratedOpportunity: (id: string) => HydratedOpportunity | undefined;
  getOpportunityLoadingState: (id: string) => OpportunityLoadingState;
  getOpportunityTiming: (id: string) => OpportunityTimingResponse | undefined;
  getOpportunityTimingLoadingState: (
    id: string,
  ) => OpportunityTimingLoadingState;
  getOpportunityTasksLoadingState: (id: string) => OpportunityTasksLoadingState;
  fetchOpportunityStatistics: (
    search?: string,
    filters?: OpportunityBoardFilters,
    companyId?: string,
  ) => Promise<void>;
  fetchOpportunityPeopleFilterCounts: (
    search?: string,
    filters?: OpportunityBoardFilters,
  ) => Promise<void>;
  /**
   * Fetches the companies selectable in the board's company filter - those
   * attached to opportunities in the caller's opportunity read scope - merging
   * them into the companies store and returning their ids in display order.
   */
  fetchOpportunityCompanyFilterOptions: (params?: {
    search?: string;
    limit?: number;
  }) => Promise<string[]>;
  fetchOpportunityTiming: (id: string) => Promise<void>;
  fetchOpportunityTasks: (id: string) => Promise<void>;
  fetchOpportunities: (
    search?: string,
    params?: {
      limit?: number;
      offset?: number;
      statuses?: OpportunityStatus[];
      stageIds?: string[];
    } & OpportunityBoardFilters,
  ) => Promise<{ ids: string[]; total: number }>;
  getOpportunity: (id: string) => Promise<void>;
  createOpportunity: (data: CreateOpportunityParams) => Promise<{
    opportunity: HydratedOpportunity | null;
    error: string | null;
  }>;
  updateOpportunity: (
    id: string,
    data: UpdateOpportunityParams,
  ) => Promise<{
    opportunity: HydratedOpportunity | null;
    error: string | null;
    missingFields: string[];
  }>;
  archiveOpportunity: (
    id: string,
  ) => Promise<{ success: boolean; error?: string }>;
  unarchiveOpportunity: (
    id: string,
  ) => Promise<{ success: boolean; error?: string }>;
  mergeOpportunities: (opportunities: HydratedOpportunity[]) => void;
  /**
   * Polls the currently-working opportunities
   * (`GET /opportunities?isPrimaryAgentThreadWorking=true`) and reconciles the
   * `isPrimaryThreadWorking` flag across the whole opportunities map: rows
   * returned are flagged working (and added if unknown), every other record's
   * flag is cleared. Late or out-of-order responses are dropped, so a stale
   * poll can never resurrect an already-cleared working state.
   */
  fetchWorkingOpportunities: () => Promise<void>;
  /** Clears every working flag and invalidates any in-flight working poll. */
  clearWorkingOpportunities: () => void;
  /**
   * Silently fetches opportunities updated after the cursor (matching the
   * board's search and person filters, with the archive flags and the
   * company/location/custom field filters left open) and merges them into the store.
   * Returns the changed records for the board to apply, or null when the
   * request failed. Never touches list loading state.
   */
  fetchOpportunityDelta: (
    updatedAfter: Date,
    search?: string,
    filters?: OpportunityBoardFilters,
  ) => Promise<HydratedOpportunity[] | null>;
}

/**
 * Page size for the live board delta poll. A delta response that fills this
 * limit means more changed than one poll can carry (e.g. the tab was hidden
 * through a bulk import), and the caller should fall back to a full refetch.
 */
export const OPPORTUNITY_DELTA_LIMIT = 100;

/**
 * Appends the active board filters (person, company, location, and custom
 * field selections, the created date range, and the archive view flags) as
 * query params.
 * @param params - The URLSearchParams to mutate
 * @param filters - The board filter selections to encode
 */
const appendBoardFilters = (
  params: URLSearchParams,
  filters?: OpportunityBoardFilters,
): void => {
  if (filters?.salesRepUserIds?.length) {
    params.set("salesRepUserIds", filters.salesRepUserIds.join(","));
  }
  if (filters?.estimatorUserIds?.length) {
    params.set("estimatorUserIds", filters.estimatorUserIds.join(","));
  }
  if (filters?.taskAssigneeUserIds?.length) {
    params.set("taskAssigneeUserIds", filters.taskAssigneeUserIds.join(","));
  }
  if (filters?.companyIds?.length) {
    params.set("companyIds", filters.companyIds.join(","));
  }
  if (filters?.locationIds?.length) {
    params.set("locationIds", filters.locationIds.join(","));
  }
  if (filters?.customFields && Object.keys(filters.customFields).length) {
    params.set("customFields", JSON.stringify(filters.customFields));
  }
  if (filters?.createdAfter) {
    params.set("createdAfter", filters.createdAfter.toISOString());
  }
  if (filters?.createdBefore) {
    params.set("createdBefore", filters.createdBefore.toISOString());
  }
  if (filters?.includeArchived) {
    params.set("includeArchived", "true");
  }
  if (filters?.archivedOnly) {
    params.set("archivedOnly", "true");
  }
};

const getInitialLoadingState = (): OpportunityLoadingState => ({
  isLoadingGet: false,
  isLoadingSave: false,
  isLoadingArchive: false,
  getError: null,
  saveError: null,
  archiveError: null,
});

const getInitialTimingLoadingState = (): OpportunityTimingLoadingState => ({
  isLoading: false,
  error: null,
});

const getInitialTasksLoadingState = (): OpportunityTasksLoadingState => ({
  isLoading: false,
  error: null,
});

/**
 * Returns a copy of the opportunities map with the incoming hydrated records
 * replacing any existing entries by id (unlike the mergeOpportunities action,
 * which shallow-merges fields into existing records).
 */
const withReplacedOpportunities = (
  current: Map<string, HydratedOpportunity>,
  incoming: HydratedOpportunity[],
): Map<string, HydratedOpportunity> => {
  const next = new Map(current);
  incoming.forEach((opportunity) => {
    next.set(opportunity.id, opportunity);
  });
  return next;
};

/**
 * Page size for the working-opportunities poll — comfortably above any
 * realistic number of concurrently running agent threads in one org.
 */
const WORKING_POLL_LIMIT = 100;

/**
 * Monotonic sequence for working-opportunity polls. Captured before each
 * request and compared after it resolves, so a late or out-of-order response
 * (or one landing after clearWorkingOpportunities) is dropped instead of
 * republishing stale working state.
 */
const workingPollSeq = { current: 0 };

/**
 * Reconciles the isPrimaryThreadWorking flag across the opportunities map:
 * records in the working set are flagged (and added when unknown), all other
 * records are cleared. Returns the input map unchanged when nothing differs,
 * so an idle poll causes no re-render. Known working records are kept as-is
 * rather than replaced, so unrelated field updates never thrash references.
 */
const applyWorkingFlags = (
  current: Map<string, HydratedOpportunity>,
  working: HydratedOpportunity[],
): Map<string, HydratedOpportunity> => {
  const workingIds = new Set(working.map((opportunity) => opportunity.id));
  const toSet: HydratedOpportunity[] = [];
  current.forEach((opportunity, id) => {
    const shouldWork = workingIds.has(id);
    if ((opportunity.isPrimaryThreadWorking ?? false) !== shouldWork) {
      toSet.push({ ...opportunity, isPrimaryThreadWorking: shouldWork });
    }
  });
  working.forEach((opportunity) => {
    if (!current.has(opportunity.id)) {
      toSet.push(opportunity);
    }
  });
  if (toSet.length === 0) {
    return current;
  }
  const next = new Map(current);
  toSet.forEach((opportunity) => next.set(opportunity.id, opportunity));
  return next;
};

/**
 * Merges related entities from hydrated opportunity responses into their stores.
 */
const mergeRelatedEntities = (
  opportunities: HydratedOpportunity | HydratedOpportunity[],
): void => {
  const opportunitiesArray = Array.isArray(opportunities)
    ? opportunities
    : [opportunities];
  const companiesToMerge: HydratedCompany[] = [];
  const locationsToMerge: Location[] = [];

  opportunitiesArray.forEach((opportunity) => {
    if (opportunity.company) {
      companiesToMerge.push(opportunity.company);
    }
    if (opportunity.location) {
      locationsToMerge.push(opportunity.location);
    }
  });

  if (companiesToMerge.length > 0) {
    useCompaniesStore.getState().mergeCompanies(companiesToMerge);
  }
  if (locationsToMerge.length > 0) {
    useLocationsStore.getState().mergeLocations(locationsToMerge);
  }
};

/**
 * Reads the field labels a stage refused over from a failed update's body.
 * @param data - The failed response body
 * @returns The missing field labels, or an empty array for any other error
 */
const parseMissingFields = (data: unknown): string[] => {
  const parsed = OpportunityStageRequirementsErrorSchema.safeParse(data);
  if (!parsed.success) {
    return [];
  }
  return parsed.data.missingFields;
};

export const useOpportunitiesStore = create<OpportunitiesStore>((set, get) => ({
  opportunities: new Map(),
  opportunityLoadingStates: new Map(),
  opportunityTimings: new Map(),
  opportunityTimingLoadingStates: new Map(),
  opportunityTasks: new Map(),
  opportunityTasksLoadingStates: new Map(),
  statistics: null,
  statisticsLoading: false,
  peopleFilterCounts: null,
  companyFilterOptionsLoading: false,
  listLoading: false,
  listError: null,
  total: 0,

  getHydratedOpportunity: (id: string) => get().opportunities.get(id),

  getOpportunityLoadingState: (id: string) =>
    get().opportunityLoadingStates.get(id) || getInitialLoadingState(),

  getOpportunityTiming: (id: string) => get().opportunityTimings.get(id),

  getOpportunityTimingLoadingState: (id: string) =>
    get().opportunityTimingLoadingStates.get(id) ||
    getInitialTimingLoadingState(),

  getOpportunityTasksLoadingState: (id: string) =>
    get().opportunityTasksLoadingStates.get(id) ||
    getInitialTasksLoadingState(),

  fetchOpportunityStatistics: async (
    search?: string,
    filters?: OpportunityBoardFilters,
    companyId?: string,
  ) => {
    set(() => ({ statisticsLoading: true }));
    try {
      const params = new URLSearchParams();
      if (search) {
        params.set("search", search);
      }
      if (companyId) {
        params.set("companyId", companyId);
      }
      appendBoardFilters(params, filters);
      const queryString = params.toString();
      const url = queryString
        ? `opportunities/statistics?${queryString}`
        : "opportunities/statistics";
      const response = await fetchApi(url, "GET");

      if (response.ok && response.data) {
        const data = GetOpportunityStatisticsResponseSchema.parse(
          response.data,
        );
        set(() => ({ statistics: data.statistics, statisticsLoading: false }));
        return;
      }

      set(() => ({ statisticsLoading: false }));
    } catch (error) {
      captureException(error as Error);
      set(() => ({ statisticsLoading: false }));
    }
  },

  fetchOpportunityPeopleFilterCounts: async (
    search?: string,
    filters?: OpportunityBoardFilters,
  ) => {
    try {
      const params = new URLSearchParams();
      if (search) {
        params.set("search", search);
      }
      appendBoardFilters(params, filters);
      const queryString = params.toString();
      const url = queryString
        ? `opportunities/people-filter-counts?${queryString}`
        : "opportunities/people-filter-counts";
      const response = await fetchApi(url, "GET");

      if (response.ok && response.data) {
        const data = GetOpportunityPeopleFilterCountsResponseSchema.parse(
          response.data,
        );
        set(() => ({ peopleFilterCounts: data.filterCounts }));
      }
    } catch (error) {
      captureException(error as Error);
    }
  },

  fetchOpportunityCompanyFilterOptions: async (params?: {
    search?: string;
    limit?: number;
  }) => {
    set(() => ({ companyFilterOptionsLoading: true }));
    try {
      const queryParams = new URLSearchParams();
      if (params?.search) {
        queryParams.set("search", params.search);
      }
      if (params?.limit !== undefined) {
        queryParams.set("limit", String(params.limit));
      }
      const queryString = queryParams.toString();
      const url = queryString
        ? `opportunities/company-filter-options?${queryString}`
        : "opportunities/company-filter-options";
      const response = await fetchApi(url, "GET");

      if (response.ok && response.data) {
        const data = GetOpportunityCompanyFilterOptionsResponseSchema.parse(
          response.data,
        );
        useCompaniesStore.getState().mergeCompanies(data.companies);
        set(() => ({ companyFilterOptionsLoading: false }));
        return data.companies.map((company) => company.id);
      }

      set(() => ({ companyFilterOptionsLoading: false }));
      return [];
    } catch (error) {
      captureException(error as Error);
      set(() => ({ companyFilterOptionsLoading: false }));
      return [];
    }
  },

  fetchOpportunityTiming: async (id: string) => {
    set((state) => {
      const loadingStates = new Map(state.opportunityTimingLoadingStates);
      loadingStates.set(id, { isLoading: true, error: null });
      return { opportunityTimingLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`opportunities/${id}/timing`, "GET");

      if (response.ok && response.data) {
        const timing = OpportunityTimingResponseSchema.parse(response.data);
        set((state) => {
          const timings = new Map(state.opportunityTimings);
          timings.set(id, timing);
          const loadingStates = new Map(state.opportunityTimingLoadingStates);
          loadingStates.set(id, { isLoading: false, error: null });
          return {
            opportunityTimings: timings,
            opportunityTimingLoadingStates: loadingStates,
          };
        });
        return;
      }

      set((state) => {
        const loadingStates = new Map(state.opportunityTimingLoadingStates);
        loadingStates.set(id, {
          isLoading: false,
          error: response.error ?? "Failed to fetch timing",
        });
        return { opportunityTimingLoadingStates: loadingStates };
      });
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.opportunityTimingLoadingStates);
        loadingStates.set(id, {
          isLoading: false,
          error: "Failed to fetch timing",
        });
        return { opportunityTimingLoadingStates: loadingStates };
      });
    }
  },

  fetchOpportunityTasks: async (id: string) => {
    set((state) => {
      const loadingStates = new Map(state.opportunityTasksLoadingStates);
      loadingStates.set(id, { isLoading: true, error: null });
      return { opportunityTasksLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`opportunities/${id}/tasks`, "GET");

      if (response.ok && response.data) {
        const data = ListOpportunityTasksResponseSchema.parse(response.data);
        set((state) => {
          const tasks = new Map(state.opportunityTasks);
          tasks.set(id, data.tasks);
          const loadingStates = new Map(state.opportunityTasksLoadingStates);
          loadingStates.set(id, { isLoading: false, error: null });
          return {
            opportunityTasks: tasks,
            opportunityTasksLoadingStates: loadingStates,
          };
        });
        return;
      }

      set((state) => {
        const loadingStates = new Map(state.opportunityTasksLoadingStates);
        loadingStates.set(id, {
          isLoading: false,
          error: response.error ?? "Failed to fetch tasks",
        });
        return { opportunityTasksLoadingStates: loadingStates };
      });
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.opportunityTasksLoadingStates);
        loadingStates.set(id, {
          isLoading: false,
          error: "Failed to fetch tasks",
        });
        return { opportunityTasksLoadingStates: loadingStates };
      });
    }
  },

  fetchOpportunities: async (
    search?: string,
    paginationParams?: {
      limit?: number;
      offset?: number;
      statuses?: OpportunityStatus[];
      stageIds?: string[];
    } & OpportunityBoardFilters,
  ) => {
    set(() => ({ listLoading: true, listError: null }));

    try {
      const params = new URLSearchParams();
      if (search) {
        params.set("search", search);
      }
      if (paginationParams?.limit !== undefined) {
        params.set("limit", String(paginationParams.limit));
      }
      if (paginationParams?.offset !== undefined) {
        params.set("offset", String(paginationParams.offset));
      }
      if (paginationParams?.statuses?.length) {
        params.set("statuses", paginationParams.statuses.join(","));
      }
      if (paginationParams?.stageIds?.length) {
        params.set("stageIds", paginationParams.stageIds.join(","));
      }
      appendBoardFilters(params, paginationParams);
      const queryString = params.toString();
      const url = queryString
        ? `opportunities?${queryString}`
        : "opportunities";
      const response = await fetchApi(url, "GET");

      if (response.ok && response.data) {
        const data = ListOpportunitiesResponseSchema.parse(response.data);
        mergeRelatedEntities(data.opportunities);
        set((state) => ({
          opportunities: withReplacedOpportunities(
            state.opportunities,
            data.opportunities,
          ),
          listLoading: false,
          total: data.total,
        }));
        return {
          ids: data.opportunities.map((opportunity) => opportunity.id),
          total: data.total,
        };
      }

      set(() => ({
        listError: "Failed to fetch opportunities",
        listLoading: false,
      }));
      return { ids: [], total: 0 };
    } catch (error) {
      captureException(error as Error);
      set(() => ({
        listError: "Failed to fetch opportunities",
        listLoading: false,
      }));
      return { ids: [], total: 0 };
    }
  },

  getOpportunity: async (id: string) => {
    set((state) => {
      const loadingStates = new Map(state.opportunityLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingGet: true,
        getError: null,
      });
      return { opportunityLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`opportunities/${id}`, "GET");

      if (response.ok && response.data) {
        const data = GetOpportunityResponseSchema.parse(response.data);
        mergeRelatedEntities(data.opportunity);
        set((state) => {
          const opportunitiesMap = new Map(state.opportunities);
          const loadingStates = new Map(state.opportunityLoadingStates);
          const currentState =
            loadingStates.get(id) || getInitialLoadingState();
          opportunitiesMap.set(id, data.opportunity);
          loadingStates.set(id, {
            ...currentState,
            isLoadingGet: false,
            getError: null,
          });
          return {
            opportunities: opportunitiesMap,
            opportunityLoadingStates: loadingStates,
          };
        });
        return;
      }

      set((state) => {
        const loadingStates = new Map(state.opportunityLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingGet: false,
          getError: response.error ?? "Failed to fetch opportunity",
        });
        return { opportunityLoadingStates: loadingStates };
      });
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.opportunityLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingGet: false,
          getError: "Failed to fetch opportunity",
        });
        return { opportunityLoadingStates: loadingStates };
      });
    }
  },

  createOpportunity: async (data: CreateOpportunityParams) => {
    try {
      const response = await fetchApi("opportunities", "POST", {
        body: data,
      });

      if (response.ok && response.data) {
        const result = CreateOpportunityResponseSchema.parse(response.data);
        mergeRelatedEntities(result.opportunity);
        set((state) => {
          const opportunitiesMap = new Map(state.opportunities);
          opportunitiesMap.set(result.opportunity.id, result.opportunity);
          return { opportunities: opportunitiesMap };
        });
        return { opportunity: result.opportunity, error: null };
      }

      return {
        opportunity: null,
        error: response.error ?? "Failed to create opportunity",
      };
    } catch (error) {
      captureException(error as Error);
      return { opportunity: null, error: "Failed to create opportunity" };
    }
  },

  updateOpportunity: async (id: string, data: UpdateOpportunityParams) => {
    set((state) => {
      const loadingStates = new Map(state.opportunityLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingSave: true,
        saveError: null,
      });
      return { opportunityLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`opportunities/${id}`, "PUT", {
        body: data,
      });

      if (response.ok && response.data) {
        const result = UpdateOpportunityResponseSchema.parse(response.data);
        mergeRelatedEntities(result.opportunity);
        set((state) => {
          const opportunitiesMap = new Map(state.opportunities);
          const loadingStates = new Map(state.opportunityLoadingStates);
          const currentState =
            loadingStates.get(id) || getInitialLoadingState();

          if (opportunitiesMap.has(id)) {
            opportunitiesMap.set(id, result.opportunity);
          }

          loadingStates.set(id, {
            ...currentState,
            isLoadingSave: false,
            saveError: null,
          });
          return {
            opportunities: opportunitiesMap,
            opportunityLoadingStates: loadingStates,
          };
        });
        return {
          opportunity: result.opportunity,
          error: null,
          missingFields: [],
        };
      }

      const errorMessage = response.error ?? "Failed to update opportunity";
      set((state) => {
        const loadingStates = new Map(state.opportunityLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: errorMessage,
        });
        return { opportunityLoadingStates: loadingStates };
      });
      return {
        opportunity: null,
        error: errorMessage,
        missingFields: parseMissingFields(response.data),
      };
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.opportunityLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingSave: false,
          saveError: "Failed to update opportunity",
        });
        return { opportunityLoadingStates: loadingStates };
      });
      return {
        opportunity: null,
        error: "Failed to update opportunity",
        missingFields: [],
      };
    }
  },

  archiveOpportunity: async (id: string) => {
    set((state) => {
      const loadingStates = new Map(state.opportunityLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingArchive: true,
        archiveError: null,
      });
      return { opportunityLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`opportunities/${id}/archive`, "POST");

      if (response.ok && response.data) {
        const result = UpdateOpportunityResponseSchema.parse(response.data);
        mergeRelatedEntities(result.opportunity);
        set((state) => {
          const opportunitiesMap = new Map(state.opportunities);
          const loadingStates = new Map(state.opportunityLoadingStates);
          opportunitiesMap.set(id, result.opportunity);
          loadingStates.set(id, getInitialLoadingState());
          return {
            opportunities: opportunitiesMap,
            opportunityLoadingStates: loadingStates,
          };
        });
        return { success: true };
      }

      set((state) => {
        const loadingStates = new Map(state.opportunityLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingArchive: false,
          archiveError: "Failed to archive opportunity",
        });
        return { opportunityLoadingStates: loadingStates };
      });
      return { success: false, error: "Failed to archive opportunity" };
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.opportunityLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingArchive: false,
          archiveError: "Failed to archive opportunity",
        });
        return { opportunityLoadingStates: loadingStates };
      });
      return { success: false, error: "Failed to archive opportunity" };
    }
  },

  unarchiveOpportunity: async (id: string) => {
    set((state) => {
      const loadingStates = new Map(state.opportunityLoadingStates);
      const currentState = loadingStates.get(id) || getInitialLoadingState();
      loadingStates.set(id, {
        ...currentState,
        isLoadingArchive: true,
        archiveError: null,
      });
      return { opportunityLoadingStates: loadingStates };
    });

    try {
      const response = await fetchApi(`opportunities/${id}/unarchive`, "POST");

      if (response.ok && response.data) {
        const result = UpdateOpportunityResponseSchema.parse(response.data);
        mergeRelatedEntities(result.opportunity);
        set((state) => {
          const opportunitiesMap = new Map(state.opportunities);
          const loadingStates = new Map(state.opportunityLoadingStates);
          opportunitiesMap.set(id, result.opportunity);
          loadingStates.set(id, getInitialLoadingState());
          return {
            opportunities: opportunitiesMap,
            opportunityLoadingStates: loadingStates,
          };
        });
        return { success: true };
      }

      set((state) => {
        const loadingStates = new Map(state.opportunityLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingArchive: false,
          archiveError: "Failed to unarchive opportunity",
        });
        return { opportunityLoadingStates: loadingStates };
      });
      return { success: false, error: "Failed to unarchive opportunity" };
    } catch (error) {
      captureException(error as Error);
      set((state) => {
        const loadingStates = new Map(state.opportunityLoadingStates);
        const currentState = loadingStates.get(id) || getInitialLoadingState();
        loadingStates.set(id, {
          ...currentState,
          isLoadingArchive: false,
          archiveError: "Failed to unarchive opportunity",
        });
        return { opportunityLoadingStates: loadingStates };
      });
      return { success: false, error: "Failed to unarchive opportunity" };
    }
  },

  mergeOpportunities: (opportunitiesToMerge: HydratedOpportunity[]) => {
    mergeRelatedEntities(opportunitiesToMerge);
    set((state) => {
      const opportunities = new Map(state.opportunities);
      opportunitiesToMerge.forEach((incoming) => {
        const existing = opportunities.get(incoming.id);
        opportunities.set(
          incoming.id,
          existing ? { ...existing, ...incoming } : incoming,
        );
      });
      return { opportunities };
    });
  },

  fetchWorkingOpportunities: async () => {
    const seq = ++workingPollSeq.current;
    try {
      const response = await fetchApi(
        `opportunities?isPrimaryAgentThreadWorking=true&limit=${WORKING_POLL_LIMIT}`,
        "GET",
      );
      if (seq !== workingPollSeq.current || !response.ok || !response.data) {
        return;
      }
      const data = ListOpportunitiesResponseSchema.parse(response.data);
      mergeRelatedEntities(data.opportunities);
      set((state) => ({
        opportunities: applyWorkingFlags(
          state.opportunities,
          data.opportunities,
        ),
      }));
    } catch (error) {
      captureException(error as Error);
    }
  },

  clearWorkingOpportunities: () => {
    workingPollSeq.current += 1;
    set((state) => ({
      opportunities: applyWorkingFlags(state.opportunities, []),
    }));
  },

  fetchOpportunityDelta: async (
    updatedAfter: Date,
    search?: string,
    filters?: OpportunityBoardFilters,
  ) => {
    try {
      const params = new URLSearchParams();
      params.set("updatedAfter", updatedAfter.toISOString());
      params.set("limit", String(OPPORTUNITY_DELTA_LIMIT));
      if (search) {
        params.set("search", search);
      }
      // Archive flags are forced open so the delta also reports opportunities
      // that were archived or unarchived, and the company/location/custom field
      // filters are dropped so a row moved off the selected values still
      // arrives; otherwise the server would omit it and the stale card would
      // linger on the board. The client's matchesBoardFilters decides
      // visibility for all of them. Person filters and search stay server-side.
      appendBoardFilters(params, {
        ...filters,
        companyIds: undefined,
        locationIds: undefined,
        customFields: undefined,
        includeArchived: true,
        archivedOnly: false,
      });
      const response = await fetchApi(
        `opportunities?${params.toString()}`,
        "GET",
      );

      if (!response.ok || !response.data) {
        return null;
      }
      const data = ListOpportunitiesResponseSchema.parse(response.data);
      if (data.opportunities.length > 0) {
        mergeRelatedEntities(data.opportunities);
        set((state) => ({
          opportunities: withReplacedOpportunities(
            state.opportunities,
            data.opportunities,
          ),
        }));
      }
      return data.opportunities;
    } catch (error) {
      captureException(error as Error);
      return null;
    }
  },
}));
